import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../components/Icon.tsx";
import { Empty, ErrorView, Field, Loading, Page, Section, Sheet, useFail, useToast } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { dateShort, label, when } from "../lib/format.ts";

const TABS = [["team", "Team"], ["devices", "Devices"], ["area", "Ben's area"], ["sharing", "Sharing"], ["accounts", "Connected accounts"], ["roster", "Roster"], ["audit", "Audit log"]] as const;

export function Settings() {
  const [tab, setTab] = useState<string>("team");
  return (
    <Page title="Settings">
      <div className="tabs" style={{ marginTop: 0 }}>{TABS.map(([k, l]) => <button key={k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{l}</button>)}</div>
      {tab === "team" && <Team />}
      {tab === "devices" && <Devices />}
      {tab === "area" && <Areas />}
      {tab === "sharing" && <Sharing />}
      {tab === "accounts" && <Accounts />}
      {tab === "roster" && <Roster />}
      {tab === "audit" && <Audit />}
    </Page>
  );
}

function Team() {
  const fail = useFail();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["users"], queryFn: () => api.get<any[]>("/users") });
  const [invite, setInvite] = useState<any>(null);
  const [adding, setAdding] = useState(false);
  const makeInvite = async (u: any) => { try { setInvite({ ...(await api.post(`/users/${u.id}/invite-code`)), name: u.name }); } catch (e) { fail(e); } };
  if (q.isLoading) return <Loading />;
  return (
    <Section title="Users and roles" count={q.data?.length} more={<button className="blue" style={{ background: "none", border: 0 }} onClick={() => setAdding(true)}>Add</button>}>
      <div className="list">{(q.data ?? []).map((u) => (
        <div key={u.id} className="row">
          <div className="main-col"><div className="title">{u.name}{!u.is_active && <span className="badge" style={{ marginLeft: 8 }}>Off</span>}</div>
            <div className="sub">{label(u.role)} · {u.email} · signs in with {u.sign_in === "google" ? "Google" : "a passkey or code"}{u.pending_devices ? ` · ${u.pending_devices} device waiting` : ""}</div></div>
          {u.sign_in === "passkey" && <button className="btn ghost sm" onClick={() => makeInvite(u)}>Invite code</button>}
        </div>
      ))}</div>
      <div className="pad muted" style={{ fontSize: 13, marginTop: 10 }}>Owner: everything. Admin: everything except settings and billing. Agent: their own leads and their area.</div>
      {invite && (
        <Sheet title={`Invite code for ${invite.name}`} onClose={() => setInvite(null)} foot={<button className="btn" onClick={() => setInvite(null)}>Done</button>}>
          <div className="code-input" style={{ fontSize: 40, margin: "8px 0 16px" }}>{invite.code}</div>
          <p className="muted">{invite.note} It lasts {invite.expires_in_hours} hours and works with {invite.email}. Nothing is sent: tell them yourself.</p>
        </Sheet>
      )}
      {adding && <AddUserSheet onClose={() => setAdding(false)} onDone={() => qc.invalidateQueries({ queryKey: ["users"] })} />}
    </Section>
  );
}

function AddUserSheet({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const fail = useFail();
  const [f, setF] = useState({ name: "", email: "", role: "agent", sign_in: "passkey", mobile: "" });
  const save = async () => { try { await api.post("/users", { ...f, mobile: f.mobile || null }); onDone(); onClose(); } catch (e) { fail(e); } };
  return (
    <Sheet title="Add a user" onClose={onClose} foot={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn" disabled={!f.name || !f.email} onClick={save}>Add</button></>}>
      <Field label="Name"><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
      <Field label="Email (their own address)"><input className="input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
      <div className="grid2">
        <Field label="Role"><select className="select" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}><option value="agent">Agent</option><option value="admin">Admin</option></select></Field>
        <Field label="Signs in with"><select className="select" value={f.sign_in} onChange={(e) => setF({ ...f, sign_in: e.target.value })}><option value="passkey">Passkey or code</option><option value="google">Google</option></select></Field>
      </div>
    </Sheet>
  );
}

function Devices() {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const q = useQuery({ queryKey: ["devices"], queryFn: () => api.get<any[]>("/devices") });
  const act = async (id: string, action: "approve" | "revoke") => {
    try { await api.patch(`/devices/${id}`, { action }); toast(action === "approve" ? "Approved" : "Signed out and blocked"); qc.invalidateQueries({ queryKey: ["devices"] }); qc.invalidateQueries({ queryKey: ["users"] }); } catch (e) { fail(e); }
  };
  if (q.isLoading) return <Loading />;
  const pending = (q.data ?? []).filter((d) => !d.approved_at && !d.revoked_at);
  const rest = (q.data ?? []).filter((d) => d.approved_at || d.revoked_at);
  const row = (d: any) => (
    <div key={d.id} className="row">
      <Icon name="devices" className="muted" />
      <div className="main-col"><div className="title">{d.user_name} · {d.label}{d.this_device && <span className="badge blue" style={{ marginLeft: 8 }}>This one</span>}</div>
        <div className="sub">Ends {d.id.slice(-6)} · {d.revoked_at ? `blocked ${when(d.revoked_at)}` : d.approved_at ? `approved, last used ${when(d.last_seen_at)}` : `asked ${when(d.created_at)}`}</div></div>
      {!d.approved_at && !d.revoked_at && <button className="btn sm" onClick={() => act(d.id, "approve")}>Approve</button>}
      {!d.revoked_at && !d.this_device && <button className="btn danger sm" onClick={() => act(d.id, "revoke")}>Block</button>}
    </div>
  );
  return (
    <>
      <Section title="Waiting for you" count={pending.length}>{pending.length ? <div className="list">{pending.map(row)}</div> : <Empty>No new phones or computers waiting.</Empty>}</Section>
      <Section title="All devices" count={rest.length}><div className="list">{rest.map(row)}</div></Section>
    </>
  );
}

function Areas() {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const q = useQuery({ queryKey: ["areas"], queryFn: () => api.get<any[]>("/areas") });
  const [edit, setEdit] = useState<any>(null);
  const [text, setText] = useState("");
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorView error={q.error} />;
  const save = async () => {
    const suburbs = text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const m = l.match(/^(.*?)[,\s]+(\d{4})$/); return m ? { suburb: m[1].trim(), postcode: m[2] } : null; });
    if (suburbs.some((s) => !s)) { toast("Each line needs a suburb and a postcode, like: Goodna, 4300", true); return; }
    try { await api.put(`/areas/${edit.id}/suburbs`, { suburbs }); toast("Area saved"); setEdit(null); qc.invalidateQueries({ queryKey: ["areas"] }); } catch (e) { fail(e); }
  };
  return (
    <>
      {(q.data ?? []).map((a) => (
        <Section key={a.id} title={a.name} count={a.suburbs.length} more={<button className="blue" style={{ background: "none", border: 0 }} onClick={() => { setEdit(a); setText(a.suburbs.map((s: any) => `${s.suburb}, ${s.postcode}`).join("\n")); }}>Edit</button>}>
          <div className="pad muted" style={{ fontSize: 13.5, marginBottom: 8 }}>Covered by {a.users.map((u: any) => u.name).join(", ") || "nobody"}. They see people, listings and seller deals on properties in these suburbs.</div>
          <div className="pad badges">{a.suburbs.map((s: any) => <span key={s.suburb + s.postcode} className="badge">{s.suburb} {s.postcode}</span>)}</div>
        </Section>
      ))}
      {edit && (
        <Sheet title={`Suburbs in ${edit.name}`} onClose={() => setEdit(null)} foot={<><button className="btn ghost" onClick={() => setEdit(null)}>Cancel</button><button className="btn" onClick={save}>Save</button></>}>
          <Field label="One per line: suburb, postcode"><textarea className="textarea" style={{ minHeight: 280 }} value={text} onChange={(e) => setText(e.target.value)} /></Field>
        </Sheet>
      )}
    </>
  );
}

function Sharing() {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const q = useQuery({ queryKey: ["shares"], queryFn: () => api.get<any[]>("/shares") });
  const users = useQuery({ queryKey: ["users"], queryFn: () => api.get<any[]>("/users") });
  const [adding, setAdding] = useState(false);
  const [who, setWho] = useState("");
  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const people = useQuery({ queryKey: ["people-pick", search], queryFn: () => api.get(`/people?q=${encodeURIComponent(search)}&limit=8`), enabled: search.length >= 2 });
  const agents = (users.data ?? []).filter((u) => u.role === "agent");
  const remove = async (s: any) => { try { await api.del("/shares", { user_id: s.user_id, entity_type: s.entity_type, entity_id: s.entity_id }); toast("No longer shared"); qc.invalidateQueries({ queryKey: ["shares"] }); } catch (e) { fail(e); } };
  const share = async (personId: string) => {
    try { await api.post("/shares", { user_id: who || agents[0]?.id, entity_type: "person", entity_id: personId, note: note || undefined }); toast("Shared"); setAdding(false); qc.invalidateQueries({ queryKey: ["shares"] }); } catch (e) { fail(e); }
  };
  if (q.isLoading) return <Loading />;
  return (
    <Section title="Records shared with an agent" count={q.data?.length} more={<button className="blue" style={{ background: "none", border: 0 }} onClick={() => setAdding(true)}>Share someone</button>}>
      {(q.data ?? []).length === 0 ? <Empty>Nothing shared.</Empty> : (
        <div className="list">{q.data!.map((s) => (
          <div key={s.user_id + s.entity_id} className="row">
            <div className="main-col"><div className="title">{s.label} → {s.user_name}</div><div className="sub">{s.entity_type}{s.note ? ` · ${s.note}` : ""} · {when(s.granted_at)}</div></div>
            <button className="btn ghost sm" onClick={() => remove(s)}>Stop</button>
          </div>
        ))}</div>
      )}
      <div className="pad muted" style={{ fontSize: 13, marginTop: 10 }}>A shared buyer is visible to the agent, but messages about listings they can't see stay hidden.</div>
      {adding && (
        <Sheet title="Share a person" onClose={() => setAdding(false)}>
          <Field label="With"><select className="select" value={who} onChange={(e) => setWho(e.target.value)}>{agents.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></Field>
          <Field label="Why (optional)"><input className="input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          <Field label="Who"><input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search people" /></Field>
          {(people.data?.rows ?? []).map((p: any) => <button key={p.id} className="row" onClick={() => share(p.id)}><div className="main-col"><div className="title">{p.display_name}</div></div><Icon name="share" /></button>)}
        </Sheet>
      )}
    </Section>
  );
}

function Accounts() {
  const fail = useFail();
  const q = useQuery({ queryKey: ["integrations"], queryFn: () => api.get("/integrations") });
  const NAMES: Record<string, string> = { google: "Google (Gmail and Calendar)", twilio: "Twilio (0485 022 805)", meta: "Facebook and Instagram", rex: "Rex", xero: "Xero", domain: "Domain", anthropic: "AI drafts (Phase 3)", embeddings: "Brain index (Phase 3)" };
  if (q.isLoading) return <Loading />;
  return (
    <>
      <div className="banner blue"><Icon name="lock" size={18} /><span><b>Sending is {q.data.sending}.</b> This copy connects to nothing. Accounts are connected on the server, one at a time with your OK (see PHASE1-VPS-PLAN.md).</span></div>
      <Section title="Connected accounts">
        <div className="list">{q.data.providers.map((p: any) => (
          <div key={p.provider} className="row">
            <div className="main-col"><div className="title">{NAMES[p.provider] ?? p.provider}</div><div className="sub">{p.accounts.length ? p.accounts.map((a: any) => `${a.label ?? ""} ${a.status}`).join(", ") : "Not connected"}</div></div>
            <button className="btn ghost sm" onClick={async () => { try { await api.post(`/integrations/${p.provider}/connect`); } catch (e) { fail(e); } }}>Connect</button>
          </div>
        ))}</div>
      </Section>
      <Section title="Sync status">
        <div className="list">{q.data.sync.map((s: any) => (
          <div key={s.connector} className="row"><div className="main-col"><div className="title">{s.connector}</div><div className="sub">{s.last_error ? `Error: ${s.last_error}` : `Last good run ${when(s.last_ok_at)}`}</div></div></div>
        ))}</div>
      </Section>
      <Section title="Lines and inboxes">
        <div className="list">{q.data.channels.map((c: any) => (
          <div key={c.kind + c.address} className="row"><div className="main-col"><div className="title">{c.label}</div><div className="sub">{c.kind.replace(/_/g, " ")} · {c.address}</div></div></div>
        ))}</div>
      </Section>
    </>
  );
}

function Roster() {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const q = useQuery({ queryKey: ["settings"], queryFn: () => api.get<any[]>("/settings") });
  const cur = q.data?.find((s) => s.key === "roster")?.value;
  const [f, setF] = useState<any>(null);
  const v = f ?? cur ?? { start: new Date().toISOString().slice(0, 10), weeks_on: 2, weeks_off: 2, on_label: "On-site week", off_label: "Home week" };
  const save = async () => { try { await api.put("/settings/roster", { value: { ...v, note: undefined } }); toast("Roster saved"); qc.invalidateQueries({ queryKey: ["settings"] }); qc.invalidateQueries({ queryKey: ["today"] }); } catch (e) { fail(e); } };
  if (q.isLoading) return <Loading />;
  return (
    <Section title="Your roster">
      <div className="pad">
        <p className="muted" style={{ marginTop: 0, fontSize: 14 }}>Today shows whether it's an on-site or a home week. {cur?.note}</p>
        <Field label="First day of an on-site stretch"><input className="input" type="date" value={v.start} onChange={(e) => setF({ ...v, start: e.target.value })} /></Field>
        <div className="grid2">
          <Field label="Weeks on site"><input className="input" inputMode="numeric" value={v.weeks_on} onChange={(e) => setF({ ...v, weeks_on: Number(e.target.value.replace(/\D/g, "")) || 0 })} /></Field>
          <Field label="Weeks at home"><input className="input" inputMode="numeric" value={v.weeks_off} onChange={(e) => setF({ ...v, weeks_off: Number(e.target.value.replace(/\D/g, "")) || 0 })} /></Field>
        </div>
        <button className="btn" onClick={save}>Save roster</button>
      </div>
    </Section>
  );
}

function Audit() {
  const [type, setType] = useState("");
  const q = useQuery({ queryKey: ["audit", type], queryFn: () => api.get<any[]>(`/audit?limit=150${type ? `&entity_type=${type}` : ""}`) });
  return (
    <Section title="Every change, newest first">
      <div className="chips">{["", "people", "opportunities", "notes", "documents", "user", "user_device", "tasks"].map((t) => <button key={t} className={"chip" + (type === t ? " on" : "")} onClick={() => setType(t)}>{t ? t.replace(/_/g, " ") : "Everything"}</button>)}</div>
      {q.isLoading ? <Loading /> : (
        <div className="scroll-x pad"><table className="table">
          <thead><tr><th>When</th><th>Who</th><th>What</th><th>Record</th></tr></thead>
          <tbody>{(q.data ?? []).map((a) => (
            <tr key={a.id}><td className="num" style={{ whiteSpace: "nowrap" }}>{dateShort(a.at.slice(0, 10))} {new Date(a.at).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}</td>
              <td>{a.actor_name ?? a.actor_kind}</td><td>{a.action.toLowerCase().replace(/_/g, " ")}</td>
              <td className="muted">{a.entity_type}{a.changes && a.action === "UPDATE" ? `: ${Object.keys(a.changes).join(", ")}` : ""}</td></tr>
          ))}</tbody>
        </table></div>
      )}
      <div className="pad muted" style={{ fontSize: 13, margin: "10px 0" }}>Nobody can edit or delete an entry, including the database owner. <Link className="blue" to="/settings">Only you can read it.</Link></div>
    </Section>
  );
}

